import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CommonPageHeader from '../components/common/CommonPageHeader';
import { 
  DocumentArrowDownIcon,
  FunnelIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import SearchBar from '../components/common/SearchBar';
import toast from 'react-hot-toast';
import { collection, query, where, getDocs, orderBy, updateDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { sendGeneralEmail } from '../services/emailService';

const ATTACHMENT_SERVICE_URL = process.env.REACT_APP_ATTACHMENT_SERVICE_URL || 'https://invoice-attachment-service-789959682965.europe-west1.run.app';

interface InvoiceAttachment {
  id: string;
  attachmentCode: string;
  customerId: string;
  customerName: string;
  year: number;
  month: number;
  sequenceNumber: number;
  storageUrl: string;
  format?: 'pdf' | 'excel' | 'invoice';
  pdfBase64?: string;
  invoiceNumber?: string;
  status?: 'active' | 'invalid';
  hidden?: boolean;
  createdAt: Date;
}

const StatusBadge: React.FC<{ status?: 'active' | 'invalid' }> = ({ status }) => {
  if (status === 'invalid') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
        Érvénytelen
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
      Aktív
    </span>
  );
};

const FormatBadge: React.FC<{ format?: 'pdf' | 'excel' | 'invoice' }> = ({ format }) => {
  if (format === 'invoice') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
        <DocumentArrowDownIcon className="h-3 w-3 mr-1" />
        Számla
      </span>
    );
  }
  if (format === 'pdf') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
        <DocumentArrowDownIcon className="h-3 w-3 mr-1" />
        PDF
      </span>
    );
  }
  if (format === 'excel') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
        <DocumentArrowDownIcon className="h-3 w-3 mr-1" />
        Excel
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
      <DocumentArrowDownIcon className="h-3 w-3 mr-1" />
      Ismeretlen
    </span>
  );
};

const InvoiceAttachments = () => {
  const { user } = useAuth();
  const [attachments, setAttachments] = useState<InvoiceAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
  const [groupByCustomer, setGroupByCustomer] = useState(true);
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState<string | null>(null);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  const [customerDataMap, setCustomerDataMap] = useState<Map<string, { id: string; email: string }>>(new Map());
  const [showHidden, setShowHidden] = useState(false);

  const handleHideAttachment = async (attachment: InvoiceAttachment) => {
    try {
      const newHidden = !attachment.hidden;

      if (attachment.id.includes('-invoice') || attachment.id.includes('-pdf') || attachment.id.includes('-excel')) {
        // Invoice doc - update the parent invoice document
        const parentDocId = attachment.id.replace(/-(invoice|pdf|excel)$/, '');
        await updateDoc(doc(db, 'invoices', parentDocId), {
          hidden: newHidden,
          updatedAt: new Date()
        });
      } else {
        // invoiceAttachments doc - update directly
        await updateDoc(doc(db, 'invoiceAttachments', attachment.id), {
          hidden: newHidden,
          updatedAt: new Date()
        });
      }

      setAttachments(prev => prev.map(a => a.id === attachment.id ? { ...a, hidden: newHidden } : a));
      toast.success(newHidden ? 'Melléklet elrejtve' : 'Melléklet újra megjelenítve');
    } catch (error) {
      console.error('Hide attachment error:', error);
      toast.error('Hiba a melléklet elrejtésekor');
    }
  };

  const handleResendEmail = async (customerName: string) => {
    setResendingEmail(customerName);
    try {
      const customerInfo = customerDataMap.get(customerName);
      console.log('📧 Resend debug - customerName:', customerName, 'customerInfo:', customerInfo);
      if (!customerInfo) {
        toast.error('Ügyfél adatai nem találhatók');
        return;
      }
      if (!customerInfo.email) {
        toast.error('Ügyfél email címe üres');
        return;
      }

      // Regenerate token
      const response = await fetch(`${ATTACHMENT_SERVICE_URL}/regenerate-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: customerInfo.id,
          customerName: customerName,
          email: customerInfo.email
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Token regenerálása sikertelen');
      }

      const result = await response.json();
      const token = result.token;
      console.log('📧 Resend debug - new token:', token);

      // Send email with new token
      const portalUrl = `${window.location.origin}/download/${token}`;
      const emailPayload = {
        to: customerInfo.email,
        subject: `Számláid és mellékleteid - ${customerName}`,
        title: 'Számla letöltési link',
        message: `Kedves ${customerName}! Az alábbi linken érheted el az összes számládat és mellékletedet. A link személyre szól és 30 napig érvényes.`,
        recipient_name: customerName,
        action_url: portalUrl,
        action_text: 'Számlák megtekintése'
      };
      console.log('📧 Resend debug - email payload:', emailPayload);
      const emailResult = await sendGeneralEmail(emailPayload);
      console.log('📧 Resend debug - sendGeneralEmail result:', emailResult);

      toast.success('Email sikeresen újraküldve');
    } catch (error) {
      console.error('Error resending email:', error);
      toast.error('Hiba az email újraküldésekor');
    } finally {
      setResendingEmail(null);
    }
  };

  const handleToggleStatus = async (attachment: InvoiceAttachment) => {
    try {
      const newStatus = attachment.status === 'invalid' ? 'active' : 'invalid';
      if (attachment.id.startsWith('invoices/') || attachment.id.includes('-invoice') || attachment.id.includes('-pdf') || attachment.id.includes('-excel')) {
        // Invoice doc - update status in invoices collection (store in a subfield or separate collection)
        // For now, we can only update invoiceAttachments collection docs directly
        toast.error('Számla státuszát közvetlenül nem lehet módosítani');
        return;
      }
      await updateDoc(doc(db, 'invoiceAttachments', attachment.id), {
        status: newStatus,
        updatedAt: new Date()
      });
      setAttachments(prev => prev.map(a => a.id === attachment.id ? { ...a, status: newStatus } : a));
      toast.success(`Státusz módosítva: ${newStatus === 'active' ? 'Aktív' : 'Érvénytelen'}`);
    } catch (error) {
      console.error('Status toggle error:', error);
      toast.error('Hiba a státusz módosításakor');
    }
  };

  const handleDownload = async (attachment: InvoiceAttachment) => {
    try {
      setDownloading(attachment.id);
      
      if (attachment.format === 'invoice') {
        if (attachment.pdfBase64) {
          // Download from Base64
          const byteCharacters = atob(attachment.pdfBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const filename = attachment.invoiceNumber || attachment.attachmentCode || 'szamla';
          link.download = `${filename}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          toast.success(`Letöltve: ${filename}.pdf`);
        } else if (attachment.storageUrl) {
          // Download from storage URL
          const response = await fetch(attachment.storageUrl);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const filename = attachment.invoiceNumber || attachment.attachmentCode || 'szamla';
          link.download = `${filename}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          toast.success(`Letöltve: ${filename}.pdf`);
        } else {
          toast.error('A számla PDF nem elérhető');
        }
      } else {
        // Download from storage URL
        const response = await fetch(attachment.storageUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const ext = attachment.format === 'excel' ? 'xlsx' : 'pdf';
        link.download = `${attachment.attachmentCode}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success(`Letöltve: ${attachment.attachmentCode}.${ext}`);
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Hiba a letöltés során');
    } finally {
      setDownloading(null);
    }
  };

  useEffect(() => {
    if (user?.organizationId) {
      loadAttachments();
    }
  }, [user?.organizationId]);

  const loadAttachments = async () => {
    if (!user?.organizationId) return;

    setLoading(true);
    try {
      // Load attachments
      const q = query(
        collection(db, 'invoiceAttachments'),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const attachmentsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          attachmentCode: data.attachmentCode,
          customerId: data.customerId,
          customerName: data.customerName,
          year: data.year,
          month: data.month,
          sequenceNumber: data.sequenceNumber,
          storageUrl: data.storageUrl,
          format: data.format,
          status: data.status || 'active',
          hidden: data.hidden || false,
          createdAt: data.createdAt?.toDate() || new Date()
        };
      }) as InvoiceAttachment[];

      // Build attachment lookup by code for customer name fallback
      const attachmentByCode = new Map<string, InvoiceAttachment>();
      attachmentsData.forEach(att => {
        if (att.attachmentCode) {
          attachmentByCode.set(att.attachmentCode, att);
        }
      });

      // Load customers for name fallback by tax number and by name prefix
      let customerByTax: Map<string, string> = new Map();
      let customerByPrefix: Map<string, string> = new Map();
      const newCustomerDataMap = new Map<string, { id: string; email: string }>();
      try {
        const custSnapshot = await getDocs(collection(db, 'customers'));
        custSnapshot.docs.forEach(doc => {
          const c = doc.data();
          const name = c.companyName || c.name || '';
          if (c.taxNumber && name) {
            customerByTax.set(c.taxNumber, name);
          }
          // Build prefix map: first 5 alphanumeric chars -> name
          if (name) {
            const prefix = name.replace(/[^a-zA-Z0-9]/g, '').substring(0, 5).toUpperCase();
            if (prefix && !customerByPrefix.has(prefix)) {
              customerByPrefix.set(prefix, name);
            }
          }
          // Store customer id and email for resend feature
          if (name) {
            const emails: string[] = [];
            if (c.email) emails.push(c.email);
            if (c.billingEmail) emails.push(c.billingEmail);
            if (c.additionalEmails) emails.push(...c.additionalEmails);
            newCustomerDataMap.set(name, {
              id: doc.id,
              email: emails.join(', ') || c.email || ''
            });
          }
        });
        setCustomerDataMap(newCustomerDataMap);
      } catch (e) {
        // ignore
      }

      // Load invoices
      const invoicesQuery = query(
        collection(db, 'invoices'),
        where('organizationId', '==', user.organizationId),
        orderBy('createdAt', 'desc')
      );
      
      const invoicesSnapshot = await getDocs(invoicesQuery);
      const invoiceData: InvoiceAttachment[] = [];
      invoicesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const issueDate = data.issueDate?.toDate ? data.issueDate.toDate() : new Date(data.issueDate);
        const attCode = data.attachmentCode || data.invoiceNumber || doc.id;
        const invNum = data.invoiceNumber || attCode;
        // Fallback chain for customer name
        const fallbackName = attachmentByCode.get(attCode)?.customerName
          || attachmentByCode.get(data.attachmentCode)?.customerName
          || attachmentByCode.get(data.invoiceNumber)?.customerName;
        const nestedBuyerName = data.buyer?.name;
        const taxFallback = customerByTax.get(data.buyerTaxNumber || data.buyer?.taxNumber || '');
        const codePrefix = attCode.split('-')[0]?.toUpperCase();
        const prefixFallback = codePrefix ? customerByPrefix.get(codePrefix) : undefined;
        const customerName = data.buyerName || nestedBuyerName || fallbackName || taxFallback || prefixFallback || 'Ismeretlen ügyfél';
        const base = {
          customerId: data.buyerTaxNumber || data.buyer?.taxNumber || '',
          customerName,
          year: issueDate.getFullYear(),
          month: issueDate.getMonth() + 1,
          sequenceNumber: 0,
          invoiceNumber: invNum,
          createdAt: data.createdAt?.toDate() || new Date()
        };
        const hidden = data.hidden || false;
        // Invoice PDF from szamlazz.hu
        if (data.pdfBase64) {
          invoiceData.push({
            id: `${doc.id}-invoice`,
            attachmentCode: invNum,
            storageUrl: '',
            format: 'invoice' as const,
            pdfBase64: data.pdfBase64,
            hidden,
            ...base
          });
        }
        // PDF attachment
        if (data.attachmentUrl) {
          invoiceData.push({
            id: `${doc.id}-pdf`,
            attachmentCode: `${attCode}`,
            storageUrl: data.attachmentUrl,
            format: 'pdf' as const,
            hidden,
            ...base
          });
        }
        // Excel attachment
        if (data.attachmentExcelUrl) {
          invoiceData.push({
            id: `${doc.id}-excel`,
            attachmentCode: `${attCode}`,
            storageUrl: data.attachmentExcelUrl,
            format: 'excel' as const,
            hidden,
            ...base
          });
        }
      });

      // Merge and sort by date
      const allItems = [...attachmentsData, ...invoiceData].sort(
        (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
      );

      setAttachments(allItems);
      toast.success(`${attachmentsData.length} melléklet, ${invoiceData.length} számla betöltve`);
    } catch (error) {
      console.error('Error loading attachments:', error);
      toast.error('Hiba a mellékletek betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const filteredAttachments = attachments.filter(attachment => {
    if (attachment.hidden && !showHidden) return false;

    const matchesSearch = 
      attachment.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attachment.attachmentCode.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesYear = selectedYear === null || attachment.year === selectedYear;
    const matchesMonth = selectedMonth === null || attachment.month === selectedMonth;
    const matchesCustomer = selectedCustomer === null || attachment.customerName === selectedCustomer;

    return matchesSearch && matchesYear && matchesMonth && matchesCustomer;
  });

  const years = Array.from(new Set(attachments.map(a => a.year))).sort((a, b) => b - a);
  const customers = Array.from(new Set(attachments.map(a => a.customerName))).sort();
  const months = [
    { value: 1, label: 'Január' },
    { value: 2, label: 'Február' },
    { value: 3, label: 'Március' },
    { value: 4, label: 'Április' },
    { value: 5, label: 'Május' },
    { value: 6, label: 'Június' },
    { value: 7, label: 'Július' },
    { value: 8, label: 'Augusztus' },
    { value: 9, label: 'Szeptember' },
    { value: 10, label: 'Október' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const getMonthName = (month: number) => {
    return months.find(m => m.value === month)?.label || month.toString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <CommonPageHeader
        title="Számlák és mellékletek"
        subtitle="Generált számlák és mellékletek megtekintése és letöltése"
        icon={DocumentArrowDownIcon}
      />

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            {/* Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Keresés
              </label>
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Ügyfél neve vagy melléklet kód..."
              />
            </div>

            {/* Year filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Év
              </label>
              <select
                value={selectedYear || ''}
                onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm shadow-sm"
              >
                <option value="">Összes év</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* Month filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Hónap
              </label>
              <select
                value={selectedMonth || ''}
                onChange={(e) => setSelectedMonth(e.target.value ? parseInt(e.target.value) : null)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm shadow-sm"
              >
                <option value="">Összes hónap</option>
                {months.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>

            {/* Customer filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Ügyfél
              </label>
              <select
                value={selectedCustomer || ''}
                onChange={(e) => setSelectedCustomer(e.target.value ? e.target.value : null)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm shadow-sm"
              >
                <option value="">Összes ügyfél</option>
                {customers.map(customer => (
                  <option key={customer} value={customer}>{customer}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Group toggle + show hidden toggle */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center space-x-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={groupByCustomer}
                onChange={(e) => setGroupByCustomer(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Csoportosítás ügyfél szerint</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showHidden}
                onChange={(e) => setShowHidden(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Elrejtettek mutatása</span>
            </label>
          </div>

          {/* Active filters */}
          {(searchTerm || selectedYear || selectedMonth || selectedCustomer) && (
            <div className="mt-4 flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <span className="text-sm text-gray-600">Aktív szűrők:</span>
              {searchTerm && (
                <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs">
                  Keresés: {searchTerm}
                </span>
              )}
              {selectedYear && (
                <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs">
                  {selectedYear}
                </span>
              )}
              {selectedMonth && (
                <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs">
                  {getMonthName(selectedMonth)}
                </span>
              )}
              {selectedCustomer && (
                <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs">
                  {selectedCustomer}
                </span>
              )}
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedYear(null);
                  setSelectedMonth(null);
                  setSelectedCustomer(null);
                }}
                className="text-xs text-primary-600 hover:text-primary-700"
              >
                Szűrők törlése
              </button>
            </div>
          )}
        </div>

        {/* Results count */}
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {filteredAttachments.length} melléklet találat
            {filteredAttachments.length !== attachments.length && ` (${attachments.length} összesen)`}
          </p>
        </div>

        {/* Attachments table */}
        {loading ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Mellékletek betöltése...</p>
          </div>
        ) : filteredAttachments.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <DocumentArrowDownIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {searchTerm || selectedYear || selectedMonth || selectedCustomer
                ? 'Nincs találat a megadott szűrőkkel'
                : 'Még nincsenek számlamellékletek'}
            </p>
          </div>
        ) : groupByCustomer ? (
          /* Grouped by customer view */
          <div className="space-y-4">
            {(() => {
              const grouped = new Map<string, InvoiceAttachment[]>();
              filteredAttachments.forEach(att => {
                const list = grouped.get(att.customerName) || [];
                list.push(att);
                grouped.set(att.customerName, list);
              });
              
              return Array.from(grouped.entries()).map(([customerName, customerAttachments]) => {
                const isExpanded = expandedCustomers.has(customerName);
                return (
                  <div key={customerName} className="bg-white rounded-lg shadow overflow-hidden">
                    <div
                      onClick={() => {
                        setExpandedCustomers(prev => {
                          const next = new Set(prev);
                          if (next.has(customerName)) {
                            next.delete(customerName);
                          } else {
                            next.add(customerName);
                          }
                          return next;
                        });
                      }}
                      className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <span className="text-sm font-semibold text-gray-900">{customerName}</span>
                        <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full text-xs">
                          {customerAttachments.filter(a => !a.hidden).length} / {customerAttachments.length} melléklet
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResendEmail(customerName);
                          }}
                          disabled={resendingEmail === customerName}
                          className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                        >
                          {resendingEmail === customerName ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent mr-1" />
                              Küldés...
                            </>
                          ) : (
                            <>
                              <EnvelopeIcon className="h-3 w-3 mr-1" />
                              Email újraküldése
                            </>
                          )}
                        </button>
                        {isExpanded ? (
                          <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Melléklet kód
                            </th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Formátum
                            </th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Státusz
                            </th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Időszak
                            </th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Létrehozva
                            </th>
                            <th className="px-6 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Műveletek
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {customerAttachments.map((attachment) => (
                            <tr key={attachment.id} className="hover:bg-gray-50">
                              <td className="px-6 py-3 whitespace-nowrap">
                                <div className="flex items-center">
                                  <DocumentArrowDownIcon className="h-5 w-5 text-gray-400 mr-2" />
                                  <span className="text-sm font-medium text-gray-900">
                                    {attachment.attachmentCode}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap">
                                <FormatBadge format={attachment.format} />
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap">
                                <StatusBadge status={attachment.status} />
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap">
                                <div className="flex items-center text-sm text-gray-900">
                                  <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
                                  {attachment.year}. {getMonthName(attachment.month)}
                                </div>
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                                {attachment.createdAt.toLocaleDateString('hu-HU', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap">
                                <StatusBadge status={attachment.status} />
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex items-center justify-end space-x-2">
                                  <button
                                    onClick={() => handleHideAttachment(attachment)}
                                    className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-colors ${
                                      attachment.hidden
                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                    title={attachment.hidden ? 'Megjelenítés' : 'Elrejtés'}
                                  >
                                    {attachment.hidden ? (
                                      <><EyeIcon className="h-3 w-3 mr-1" />Megjelenítés</>
                                    ) : (
                                      <><EyeSlashIcon className="h-3 w-3 mr-1" />Elrejtés</>
                                    )}
                                  </button>
                                  {!attachment.id.includes('-invoice') && !attachment.id.includes('-excel') && (
                                    <button
                                      onClick={() => handleToggleStatus(attachment)}
                                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-colors ${
                                        attachment.status === 'invalid'
                                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                                      }`}
                                    >
                                      {attachment.status === 'invalid' ? 'Aktiválás' : 'Érvénytelenítés'}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDownload(attachment)}
                                    disabled={downloading === attachment.id}
                                    className="inline-flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {downloading === attachment.id ? (
                                      <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-1" />
                                        Letöltés...
                                      </>
                                    ) : (
                                      <>
                                        <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                                        Letöltés
                                      </>
                                    )}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        ) : (
          /* Flat table view */
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Melléklet kód
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Formátum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Státusz
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ügyfél
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Időszak
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Létrehozva
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAttachments.map((attachment) => (
                  <tr key={attachment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <DocumentArrowDownIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {attachment.attachmentCode}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <FormatBadge format={attachment.format} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={attachment.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{attachment.customerName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
                        {attachment.year}. {getMonthName(attachment.month)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {attachment.createdAt.toLocaleDateString('hu-HU', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={attachment.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleHideAttachment(attachment)}
                          className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-colors ${
                            attachment.hidden
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title={attachment.hidden ? 'Megjelenítés' : 'Elrejtés'}
                        >
                          {attachment.hidden ? (
                            <><EyeIcon className="h-3 w-3 mr-1" />Megjelenítés</>
                          ) : (
                            <><EyeSlashIcon className="h-3 w-3 mr-1" />Elrejtés</>
                          )}
                        </button>
                        {!attachment.id.includes('-invoice') && !attachment.id.includes('-excel') && (
                          <button
                            onClick={() => handleToggleStatus(attachment)}
                            className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-colors ${
                              attachment.status === 'invalid'
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                          >
                            {attachment.status === 'invalid' ? 'Aktiválás' : 'Érvénytelenítés'}
                          </button>
                        )}
                        <button
                          onClick={() => handleDownload(attachment)}
                          disabled={downloading === attachment.id}
                          className="inline-flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {downloading === attachment.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-1" />
                              Letöltés...
                            </>
                          ) : (
                            <>
                              <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                              Letöltés
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceAttachments;
